import { useState } from 'react';
import {
  ChevronDown,
  ChevronUp
} from 'lucide-react';

function FAQs() {
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const faqs = [
    {
      id: 'documents',
      question: 'What documents do I need for loan approval?',
      answer: "You'll need:\n\n• Government ID proof (Aadhaar, PAN, Passport, Voter ID)\n• Address proof (Aadhaar, utility bill, rental agreement)\n• Income proof (3 months salary slips, bank statements, ITR)\n• Employment proof (offer letter, experience certificate)\n\nMost documents can be uploaded online. We'll guide you through the process."
    },
    {
      id: 'approval-time',
      question: 'How long does loan approval take?',
      answer: 'Approval timeline:\n\n• Pre-approval: 2-4 hours\n• Full approval: 24-48 hours\n• Disbursal: 2-5 working days\n\nPersonal loans are fastest, home loans take longer due to property verification. Track your application status via email/SMS.'
    },
    {
      id: 'prepayment',
      question: 'Can I prepay my loan? Are there charges?',
      answer: 'Yes! Prepayment benefits:\n\n• Zero prepayment charges on all loans\n• Partial/full prepayment allowed after 6 EMIs\n• Interest calculated only on outstanding amount\n• Save thousands in interest payments\n\nPrepay anytime via app, net banking or branch visit.'
    },
    {
      id: 'interest-rates',
      question: 'What are your current interest rates?',
      answer: 'Competitive rates starting from:\n\n• Personal Loan: 10.5% - 24% p.a.\n• Home Loan: 8.25% - 12.5% p.a.\n• Business Loan: 12% - 22% p.a.\n• Car Loan: 9.25% - 14% p.a.\n\nRates depend on credit score, income, loan amount & tenure. Get personalized quote instantly.'
    },
    {
      id: 'eligibility',
      question: 'What is the eligibility criteria?',
      answer: 'Basic eligibility:\n\n• Age: 21-60 years\n• Minimum income: ₹25,000/month (salaried)\n• Credit score: 680+ preferred\n• Employment: 1+ years experience\n• Indian citizen/permanent resident\n\nSelf-employed need 2+ years business vintage & ITR filing.'
    },
    {
      id: 'tenure',
      question: 'What loan tenures are available?',
      answer: 'Flexible repayment options:\n\n• Personal Loan: 6-60 months\n• Home Loan: 5-30 years\n• Car Loan: 12-84 months\n• Business Loan: 12-120 months\n\nChoose tenure based on your cash flow. Use our EMI calculator to find best fit.'
    },
    {
      id: 'emi-calc',
      question: 'How do I calculate my EMI?',
      answer: 'EMI Formula: P × r × (1+r)^n / ((1+r)^n - 1)\n\nWhere:\n• P = Principal loan amount\n• r = Monthly interest rate (annual rate/12/100)\n• n = Loan tenure in months\n\nUse our online EMI calculator for instant results with amortization schedule.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-800 to-blue-900 text-white py-12 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed px-4">
            Find answers to common questions about our loan products, process, rates and eligibility.
          </p>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto py-8 sm:py-12 md:py-16 px-4 sm:px-6 lg:px-8">
        <div className="space-y-3 sm:space-y-4">
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
            >
              <button
                onClick={() => toggleSection(faq.id)}
                className="w-full flex items-center justify-between p-4 sm:p-6 md:p-8 text-left hover:bg-gray-50 transition-colors duration-200 group"
              >
                <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 pr-3 sm:pr-4 flex-1 leading-snug">
                  {faq.question}
                </h3>
                <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center transition-all duration-200 group-hover:scale-110">
                  {openSections[faq.id] ? (
                    <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  ) : (
                    <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  )}
                </div>
              </button>
              
              {openSections[faq.id] && (
                <div className="px-4 pb-4 sm:px-6 sm:pb-6 md:px-8 md:pb-8">
                  <div className="pt-2 border-t border-gray-100">
                    <div className="whitespace-pre-wrap text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg max-w-none">
                      {faq.answer.split('\n').map((line, index) => (
                        <span key={index}>
                          {line}
                          <br />
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 sm:mt-16 md:mt-20 text-center">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 border border-blue-100">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
              Still have questions?
            </h3>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
              Our team is ready to help you find the perfect loan solution tailored to your needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center max-w-md mx-auto">
              <a
                href="#contact"
                className="w-full sm:w-auto bg-gradient-to-r from-blue-800 to-blue-900 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:from-blue-900 hover:to-blue-800 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl text-sm sm:text-base"
              >
                Contact Us
              </a>
              <a
                href="#apply"
                className="w-full sm:w-auto bg-white text-blue-900 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl border-2 border-blue-200 text-sm sm:text-base"
              >
                Apply Now
              </a>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default FAQs;
